import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, ShoppingCart, Truck, Receipt, RotateCcw, CreditCard, Check, ChevronRight, Circle } from 'lucide-react';

/**
 * WorkflowTimeline Component
 * Displays the sales document workflow: Quote → Order → Proforma → Delivery → Invoice → Return → Payment
 * Shows which steps are completed and allows navigation between linked documents
 */
const WorkflowTimeline = ({ 
  currentDocument,  // { type: 'QUOTATION' | 'SALES_ORDER' | 'PROFORMA' | 'DELIVERY_CHALLAN' | 'SALES_INVOICE' | 'SALES_RETURN' | 'PAYMENT', id, number }
  linkedDocuments,  // { quotation, salesOrder, proforma, deliveryChallan, invoice, salesReturn, payment }
  businessId,
  compact = false
}) => {
  const navigate = useNavigate();

  // Define workflow steps with icons and colors.
  // Routes below must match what's registered in App.js -- only invoices and
  // quotations are nested under /sales/, everything else is flat under the
  // business layout. Mismatches here cause "click a step, land on dashboard"
  // because the app falls through to the default route.
  const workflowSteps = [
    { key: 'quotation', label: 'Quote', icon: FileText, color: 'blue', route: '/sales/quotations' },
    { key: 'salesOrder', label: 'Order', icon: ShoppingCart, color: 'purple', route: '/sales-orders' },
    { key: 'proforma', label: 'Proforma', icon: Receipt, color: 'amber', route: '/proforma-invoices' },
    { key: 'deliveryChallan', label: 'Delivery', icon: Truck, color: 'teal', route: '/delivery-challans' },
    { key: 'invoice', label: 'Invoice', icon: Receipt, color: 'emerald', route: '/sales/invoices' },
    { key: 'salesReturn', label: 'Return', icon: RotateCcw, color: 'red', route: '/sales-returns' },
    { key: 'payment', label: 'Payment', icon: CreditCard, color: 'green', route: '/payments-received' },
  ];

  // Map current document type to step key
  const typeToKey = {
    'QUOTATION': 'quotation',
    'SALES_ORDER': 'salesOrder',
    'PROFORMA': 'proforma',
    'DELIVERY_CHALLAN': 'deliveryChallan',
    'SALES_INVOICE': 'invoice',
    'SALES_RETURN': 'salesReturn',
    'PAYMENT': 'payment'
  };

  const currentStepKey = typeToKey[currentDocument?.type] || 'quotation';

  // Color classes
  const colorClasses = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200', activeBg: 'bg-blue-600' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200', activeBg: 'bg-purple-600' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200', activeBg: 'bg-amber-600' },
    teal: { bg: 'bg-teal-100', text: 'text-teal-600', border: 'border-teal-200', activeBg: 'bg-teal-600' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200', activeBg: 'bg-emerald-600' },
    red: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-200', activeBg: 'bg-red-600' },
    green: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200', activeBg: 'bg-green-600' },
  };

  const handleStepClick = (step) => {
    const linkedDoc = linkedDocuments?.[step.key];
    if (linkedDoc?.id) {
      navigate(`/business/${businessId}${step.route}/${linkedDoc.id}`);
    }
  };

  if (compact) {
    // Compact horizontal view for headers
    return (
      <div className="flex items-center gap-1" data-testid="workflow-timeline-compact">
        {workflowSteps.map((step, index) => {
          const isCompleted = linkedDocuments?.[step.key]?.id;
          const isCurrent = step.key === currentStepKey;
          const colors = colorClasses[step.color];
          const StepIcon = step.icon;

          return (
            <React.Fragment key={step.key}>
              <button
                onClick={() => handleStepClick(step)}
                disabled={!isCompleted}
                className={`
                  inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-medium
                  transition-all duration-200
                  ${isCurrent 
                    ? `${colors.activeBg} text-white shadow-sm` 
                    : isCompleted 
                      ? `${colors.bg} ${colors.text} hover:opacity-80 cursor-pointer` 
                      : 'bg-slate-100 text-slate-400 cursor-default'
                  }
                `}
                title={isCompleted ? `View ${step.label}: ${linkedDocuments[step.key]?.number}` : step.label}
                data-testid={`workflow-step-${step.key}`}
              >
                <StepIcon className="w-3 h-3" />
                <span className="hidden sm:inline">{step.label}</span>
                {isCompleted && !isCurrent && <Check className="w-3 h-3" />}
              </button>
              {index < workflowSteps.length - 1 && (
                <ChevronRight className="w-3 h-3 text-slate-300 flex-shrink-0" />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // Full vertical timeline view
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid="workflow-timeline">
      <h3 className="text-[14px] font-semibold text-slate-800 mb-4">Document Workflow</h3>
      
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-[15px] top-4 bottom-4 w-0.5 bg-slate-200"></div>
        
        <div className="space-y-3">
          {workflowSteps.map((step, index) => {
            const linkedDoc = linkedDocuments?.[step.key];
            const isCompleted = linkedDoc?.id;
            const isCurrent = step.key === currentStepKey;
            const colors = colorClasses[step.color];
            const StepIcon = step.icon;

            return (
              <div key={step.key} className="relative flex items-start gap-3">
                {/* Step indicator */}
                <div className={`
                  relative z-10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                  transition-all duration-200
                  ${isCurrent 
                    ? `${colors.activeBg} text-white shadow-md shadow-${step.color}-500/30` 
                    : isCompleted 
                      ? `${colors.bg} ${colors.text}` 
                      : 'bg-slate-100 text-slate-400'
                  }
                `}>
                  {isCompleted && !isCurrent ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <StepIcon className="w-4 h-4" />
                  )}
                </div>
                
                {/* Step content */}
                <div className="flex-1 min-w-0 pb-3">
                  <div className="flex items-center justify-between">
                    <span className={`text-[13px] font-medium ${isCurrent ? 'text-slate-800' : isCompleted ? 'text-slate-700' : 'text-slate-400'}`}>
                      {step.label}
                    </span>
                    {isCompleted && (
                      <button
                        onClick={() => handleStepClick(step)}
                        className={`text-[11px] font-medium ${colors.text} hover:underline`}
                      >
                        {linkedDoc.number}
                      </button>
                    )}
                  </div>
                  
                  {isCurrent && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 mt-1 rounded-full text-[10px] font-semibold ${colors.bg} ${colors.text}`}>
                      <Circle className="w-2 h-2 fill-current" />
                      Current
                    </span>
                  )}
                  
                  {!isCompleted && !isCurrent && (
                    <span className="text-[11px] text-slate-400">Pending</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WorkflowTimeline;
